/** @file Attaches the one keydown/keyup/blur listener set to a window or document (the only such site). */
import { detectPlatform } from './detectPlatform';
import { handleBlur } from './_internal/handleBlur';
import { handleKeyDown } from './_internal/handleKeyDown';
import { handleKeyUp } from './_internal/handleKeyUp';
import { keyState, type Attachment } from './_internal/keyState';
import type { KeySourceEvent, KeyTarget, ListenOptions } from './keyTarget';

function attach(target: KeyTarget): Attachment {
  const down = (e: Event): void => { handleKeyDown(e as unknown as KeySourceEvent); };
  const up = (e: Event): void => { handleKeyUp(e as unknown as KeySourceEvent); };
  const blur = (): void => { handleBlur(); };
  target.addEventListener('keydown', down, false);
  target.addEventListener('keyup', up, false);
  target.addEventListener('blur', blur, false);
  return {
    refs: 0,
    detach: () => {
      target.removeEventListener('keydown', down, false);
      target.removeEventListener('keyup', up, false);
      target.removeEventListener('blur', blur, false);
    },
  };
}

/**
 * Idempotent per target (refcounted); returns a once-only disposer. Listens in the bubble phase (pass `window`), so keys
 * a ui-kit widget consumed with stopPropagation never arrive. Skips events while typing in inputs/textareas/
 * contenteditable or during IME composition. `options.platform`/`options.when` replace the current values when given.
 */
export function listen(target: KeyTarget, options: ListenOptions = {}): () => void {
  if (options.platform) keyState.platform = options.platform;
  else if (keyState.attachments.size === 0) keyState.platform = detectPlatform(globalThis.navigator);
  if (options.when) keyState.when = options.when;
  const entry = keyState.attachments.get(target) ?? attach(target);
  keyState.attachments.set(target, entry);
  entry.refs += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    entry.refs -= 1;
    if (entry.refs > 0) return;
    entry.detach();
    keyState.attachments.delete(target);
    if (keyState.attachments.size > 0) return;
    handleBlur();
    keyState.when = undefined;
  };
}
